import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { Schedule, getLocalTimeString } from 'src/classes/schedule';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScheduleFormModalComponent } from 'src/app/schedule-form-modal/schedule-form-modal.component';
import { ConfirmDeletionModalComponent } from 'src/app/confirm-deletion-modal/confirm-deletion-modal.component';
import { extractRuleFromScheduleData } from 'src/utils/extractRuleFromScheduleData';

let nextScheduleId = -1;

interface ScheduleData { idSchedules: number; Type: 'H' | 'D' | 'W' | 'M'; Rule: string; Status: 'A' | 'D'; }

function isScheduleData(obj: any): obj is ScheduleData {
  return (
    obj !== null &&
    obj !== undefined &&
    typeof obj === 'object' &&
    obj.Type !== undefined &&
    obj.Rule !== undefined &&
    obj.Status !== undefined
  );
}

function getRuleFromRecurrence(recurrence: string): 'H' | 'D' | 'W' | 'M' {
  switch (recurrence.toLowerCase()) {
    case 'h':
    case 'hourly': return 'H';
    case 'd':
    case 'daily': return 'D';
    case 'w':
    case 'weekly': return 'W';
    case 'm':
    case 'monthly': return 'M';
    default: return 'M';
  }
}

function splitHourMinuteZone(hourMinuteZone: string): {hour: number, minute: number, zone: string} {
  const [time, zone] = hourMinuteZone.split(' ');
  if (time === 'BRT' || time === 'UTC') {
    throw new Error('Incomplete date time');
  }
  const [hour, minute] = time.split(':').map(str => parseInt(str, 10));
  return {hour, minute, zone};
}

function convertWeekdayToNumber(weekday: string) {
  if (!isNaN(parseInt(weekday, 10))) {
    return parseInt(weekday, 10);
  }
  const lookup = {su: 0, mo: 1, tu: 2, we: 3, th: 4, fr: 5, sa: 6};
  return lookup[weekday.substring(0, 2).toLowerCase()];
}

function getRuleObject(type: 'H' | 'D' | 'W' | 'M', days: string, times: string, weekdays: string, time: string) {
  const obj: any = {};
  if (type === 'D') {
    const hourList = times.split(',');
    const timeList = hourList.map(splitHourMinuteZone);
    obj.minute = timeList[0].minute;
    obj.hour = [...new Set(timeList.map(timeObj => timeObj.hour))];
  } else if (type === 'W') {

    obj.dayOfWeek = JSON.parse(weekdays).map(i=>['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(i));
    const timeObj = splitHourMinuteZone(time);
    obj.hour = timeObj.hour;
    obj.minute = timeObj.minute;
  } else if (type === 'M') {
    var d ;
    try{
      //console.log(days.split(',').map(i=>parseInt(i)))
      d = days.split(',').map(i=>parseInt(i));
    }
    catch(e){
      d = days;
    }
    obj.date = d;
    const timeObj = splitHourMinuteZone(time);
    obj.hour = timeObj.hour;
    obj.minute = timeObj.minute;
  } else {
    throw new Error('Unimplemented');
  }
  return JSON.stringify(obj);
}

@Component({
  selector: 'app-task-step-schedule',
  templateUrl: './task-step-schedule.component.html',
  styleUrls: [
    './task-step-schedule.component.scss',
    './../task-form.component.scss'
  ]
})
export class TaskStepScheduleComponent implements OnInit {

  @Input() formGroup: FormGroup;
  @Output() addSchedule = new EventEmitter<Schedule>();
  @Output() updateSchedule = new EventEmitter<Schedule>();
  @Output() deleteSchedule = new EventEmitter<number>();

  constructor(private modalService: NgbModal) { }

  ngOnInit() {
  }

  public get schedules() {
    return this.formGroup.get('Schedules').value;
  }

  async onEditScheduleClick(scheduleData: ScheduleData) {
    await this.showScheduleFormModal(scheduleData);
  }

  async onDeleteScheduleClick(scheduleData: ScheduleData) {
    if (await this.showConfirmDeletionModal() === true) {
      this.deleteSchedule.emit(scheduleData.idSchedules);
    }
  }

  async showConfirmDeletionModal(): Promise<boolean> {
    try {
      const modalRef = this.modalService.open(ConfirmDeletionModalComponent, { ariaLabelledBy: 'modal-confirm-deletion' });
      const result = await modalRef.result;
      return result === true;
    } catch(reason) {
    }
    return false;
  }

  async onAddScheduleClick() {
    this.showScheduleFormModal(null);
  }

  async showScheduleFormModal(data: ScheduleData | null) {
    const isNew = !isScheduleData(data);
    try {
      const modalRef = this.modalService.open(ScheduleFormModalComponent, { ariaLabelledBy: 'modal-add-schedule' });

      if (!isNew) {
        const schedule = new Schedule();
        schedule.idSchedules = data.idSchedules;
        schedule.rule = data.Rule;
        schedule.status = data.Status;
        schedule.type = data.Type;
        modalRef.componentInstance.schedule = schedule;
      }

      const result = (await modalRef.result);
      if (result) {
        const schedule = new Schedule();
        schedule.type = getRuleFromRecurrence(result.Recurrence);
        schedule.status = result.Status === true || result.Status === 'A' ? 'A' : 'D';
        schedule.rule = getRuleObject(schedule.type, result.Days, result.Times, result.Weekdays, result.Time);
        if (isNew) {
          schedule.idSchedules = nextScheduleId--;
          this.addSchedule.emit(schedule);
        } else {
          schedule.idSchedules = data.idSchedules;
          this.updateSchedule.emit(schedule);
        }
      }
    } catch(reason) {
    }
  }

  updateRuleString() {
    const schedules = (this.formGroup.get('Schedules') as FormArray);
    schedules.controls.forEach(
      control => control.get('RuleString').setValue(
        this.getRuleLabel(control.get('Type').value, control.get('Rule').value)
      )
    );
  }

  public getTypeLabel(typeCode: 'H' | 'D' | 'W' | 'M'): string {
    switch (typeCode) {
      case 'H': return 'Hourly';
      case 'D': return 'Daily';
      case 'W': return 'Weekly';
      case 'M': return 'Monthly';
      default: return 'Unknown';
    }
  }

  public getRuleLabel(typeCode: 'H' | 'D' | 'W' | 'M', ruleString: string) {
    return extractRuleFromScheduleData(typeCode, ruleString);
  }

  onStatusClick(data: ScheduleData) {
    const schedule = new Schedule();
    schedule.idSchedules = data.idSchedules;
    schedule.rule = data.Rule;
    schedule.status = data.Status === 'A' ? 'D' : 'A';
    schedule.type = data.Type;
    this.updateSchedule.emit(schedule);
  }

  public getLocalTimeFromData(scheduleData: ScheduleData) {
    const schedule = new Schedule();
    schedule.rule = scheduleData.Rule;
    schedule.status = scheduleData.Status;
    schedule.type = scheduleData.Type;
    const datetime = getLocalTimeString(schedule);
    return datetime;
  }
}
